import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { intakes, zarzadyZlewni, egib, savedParcels, sieveAnalysis,
         type ZarzadZlewniDto, type EgibParcelFeatureCollection,
         type SavedParcelDto, type SavedParcelInput,
         type ParcelExportRow } from '@/api/endpoints';
import type { ParcelsOverlay } from '@/components/MapLibreMap';
import { sieveStatusPill } from '@/components/sieve/statusPill';
import type { Intake, Borehole, BoreholeLayer, SieveProjectSummary } from '@/types';
import { hasPermission } from '@/types';
import { Icon } from '@/components/Icon';
import { BoreholeProfileModal } from '@/components/BoreholeProfileModal';
import { MapLibreMap } from '@/components/MapLibreMap';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { IntakeOperatCard } from '@/components/IntakeOperatCard';
import { NewStudyModal } from '@/components/NewStudyModal';
import { IntakeMergeModal } from '@/components/IntakeMergeModal';
import { CoordsInput } from '@/components/CoordsInput';
import { SearchableSelect } from '@/components/SearchableSelect';
import { geo, boreholes as boreholesApi } from '@/api/endpoints';
import { VOIVODESHIPS, COUNTIES } from '@/data/poland-geo';
import { gminyFor, resolveGmina } from '@/data/poland-gmina';
import { patternStyle } from '@/components/HatchingFill';
import { useAuth } from '@/auth/AuthContext';

type IntakeTab = 'przeglad' | 'otwory' | 'mapa' | 'dokumenty' | 'powiazania' | 'sitowa';

/** Odmiana „próbka" przez liczebnik: 1 próbka, 2–4 próbki, 5+ (i 12–14) próbek. */
function probkiPl(n: number): string {
  if (n === 1) return 'próbka';
  const dwie = n % 10, nascie = n % 100;
  return dwie >= 2 && dwie <= 4 && (nascie < 12 || nascie > 14) ? 'próbki' : 'próbek';
}

export function IntakeDetail() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const { user } = useAuth();
  const canCreateStudy = user?.permissions?.includes('STUDY_CREATE') ?? false;
  const canViewSieve = hasPermission(user, 'SIEVE_VIEW');
  const canEditSieve = hasPermission(user, 'SIEVE_EDIT');
  const [data, setData] = useState<Intake | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [newStudyOpen, setNewStudyOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const canEditIntake = hasPermission(user, 'INTAKE_EDIT');
  const [editingBorehole, setEditingBorehole] = useState<Borehole | null>(null);
  const [profileFor, setProfileFor] = useState<number | null>(null);

  // Parcels: saved (DB) + search results (WFS, ephemeral). Buffer circle visible podczas
  // szukania albo gdy pokazujemy zapisane (defaultowo wyłączony).
  const [savedParcelsList, setSavedParcelsList] = useState<SavedParcelDto[]>([]);
  const [searchedParcels, setSearchedParcels] = useState<EgibParcelFeatureCollection | null>(null);
  const [parcelBufferM, setParcelBufferM] = useState<number>(100);
  const [parcelsBusy, setParcelsBusy] = useState(false);
  const [parcelToggleBusy, setParcelToggleBusy] = useState<string | null>(null);
  const [parcelsErr, setParcelsErr] = useState<string | null>(null);
  const [showBufferCircle, setShowBufferCircle] = useState(false);
  const [showPshMap, setShowPshMap] = useState(false);
  const [focusedParcelId, setFocusedParcelId] = useState<string | null>(null);
  const [tab, setTab] = useState<IntakeTab>('przeglad');

  // Analizy sitowe ujęcia — IntakeDto ich nie niesie, więc osobne zapytanie.
  // null = jeszcze nie pobrano (leniwie, dopiero po pierwszym wejściu w zakładkę);
  // tablica = pobrano i trzymamy, więc przełączanie zakładek już nie strzela do API.
  const [sieveList, setSieveList] = useState<SieveProjectSummary[] | null>(null);
  const [sieveBusy, setSieveBusy] = useState(false);
  const [sieveErr, setSieveErr] = useState<string | null>(null);
  /** Id ujęcia, dla którego już poszło żądanie — blokada przed ponownym strzałem przy każdym przełączeniu. */
  const sieveLoadedFor = useRef<string | null>(null);

  const reload = () => {
    if (!id) return;
    setErr(null);
    intakes.get(Number(id)).then(setData).catch(e => setErr(String(e)));
  };

  const reloadSavedParcels = (intakeId: number) => {
    savedParcels.list(intakeId)
      .then(setSavedParcelsList)
      .catch(e => setParcelsErr(String(e)));
  };

  useEffect(() => {
    if (!id) return;
    setData(null);
    setSavedParcelsList([]);
    setSearchedParcels(null);
    setShowBufferCircle(false);
    setFocusedParcelId(null);
    setTab('przeglad');
    setSieveList(null);
    setSieveErr(null);
    sieveLoadedFor.current = null;
    reload();
    reloadSavedParcels(Number(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Leniwe pobranie analiz sitowych: strzelamy dokładnie raz, przy pierwszym wejściu w zakładkę.
  // Znacznik trzymamy w ref (a nie w `sieveList`), bo po błędzie lista zostaje pusta — warunek
  // oparty na niej kręciłby żądania w kółko. Ponowienie jest ręczne, przyciskiem w banerze błędu.
  const loadSieve = (intakeId: string) => {
    sieveLoadedFor.current = intakeId;
    setSieveBusy(true);
    setSieveErr(null);
    sieveAnalysis.list(undefined, Number(intakeId))
      // Odpowiedź dla innego ujęcia (użytkownik zdążył przejść dalej) ignorujemy.
      .then(list => { if (sieveLoadedFor.current === intakeId) setSieveList(list); })
      .catch(e => { if (sieveLoadedFor.current === intakeId) setSieveErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (sieveLoadedFor.current === intakeId) setSieveBusy(false); });
  };

  useEffect(() => {
    if (tab !== 'sitowa' || !id || !canViewSieve) return;
    if (sieveLoadedFor.current === id) return;
    loadSieve(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, id, canViewSieve]);

  // === Parcels: merged overlay (saved + searched) + map handlers ===
  const savedParcelIdSet = new Set(savedParcelsList.map(p => p.egibId));

  const mergedParcelsOverlay: ParcelsOverlay | null = (() => {
    const features: ParcelsOverlay['features'] = [];
    // Najpierw zapisane — z geometriami z DB
    for (const sp of savedParcelsList) {
      if (!sp.geometry) continue;
      features.push({
        type: 'Feature',
        id: sp.egibId,
        properties: {
          id: sp.egibId,
          number: sp.parcelNumber,
          obrebNumer: sp.obrebNumer,
          obrebNazwa: sp.obrebNazwa,
          gminaNazwa: sp.gminaNazwa,
          poleEwidencyjne: sp.poleM2 != null ? String(sp.poleM2) : null,
          klasouzytki: sp.klasouzytki,
          dataEgib: sp.dataEgib,
        },
        geometry: sp.geometry as ParcelsOverlay['features'][number]['geometry'],
      });
    }
    // Następnie WFS-results, omijając te już dodane (żeby nie duplikować)
    if (searchedParcels) {
      for (const f of searchedParcels.features) {
        if (savedParcelIdSet.has(f.id)) continue;
        features.push({
          type: 'Feature',
          id: f.id,
          properties: f.properties as unknown as ParcelsOverlay['features'][number]['properties'],
          geometry: f.geometry as ParcelsOverlay['features'][number]['geometry'],
        });
      }
    }
    return features.length > 0 ? { type: 'FeatureCollection', features } : null;
  })();

  const onParcelClickToggle = async (props: Record<string, string | number | null | undefined>) => {
    if (!data) return;
    const egibId = String(props.id ?? '');
    if (!egibId) return;
    setParcelsErr(null);
    setParcelToggleBusy(egibId);
    try {
      if (savedParcelIdSet.has(egibId)) {
        await savedParcels.remove(data.id, egibId);
      } else {
        // Znajdź geometrię w wyszukanych — albo dodaj bez geometrii (mało prawdopodobne)
        const found = searchedParcels?.features.find(f => f.id === egibId);
        const input: SavedParcelInput = {
          egibId,
          parcelNumber: (props.number as string) ?? null,
          obrebNumer: (props.obrebNumer as string) ?? null,
          obrebNazwa: (props.obrebNazwa as string) ?? null,
          gminaNazwa: (props.gminaNazwa as string) ?? null,
          poleM2: props.poleEwidencyjne ? parseFloat(String(props.poleEwidencyjne)) : null,
          klasouzytki: (props.klasouzytki as string) ?? null,
          dataEgib: (props.dataEgib as string) ?? null,
          geometry: found?.geometry as SavedParcelInput['geometry'],
        };
        await savedParcels.add(data.id, input);
      }
      reloadSavedParcels(data.id);
    } catch (e) {
      setParcelsErr(e instanceof Error ? e.message : String(e));
    } finally {
      setParcelToggleBusy(null);
    }
  };

  const searchParcels = async () => {
    if (!data) return;
    setParcelsBusy(true);
    setParcelsErr(null);
    setShowBufferCircle(true);
    try {
      const fc = await egib.parcelsForIntake(data.id, parcelBufferM);
      setSearchedParcels(fc);
    } catch (e) {
      setParcelsErr(e instanceof Error ? e.message : String(e));
    } finally {
      setParcelsBusy(false);
    }
  };

  const addAllFromSearch = async () => {
    if (!data || !searchedParcels) return;
    const toAdd: SavedParcelInput[] = searchedParcels.features
      .filter(f => !savedParcelIdSet.has(f.id))
      .map(f => ({
        egibId: f.id,
        parcelNumber: f.properties.number,
        obrebNumer: f.properties.obrebNumer,
        obrebNazwa: f.properties.obrebNazwa,
        gminaNazwa: f.properties.gminaNazwa,
        poleM2: f.properties.poleEwidencyjne ? parseFloat(f.properties.poleEwidencyjne) : null,
        klasouzytki: f.properties.klasouzytki,
        dataEgib: f.properties.dataEgib,
        geometry: f.geometry as SavedParcelInput['geometry'],
      }));
    if (toAdd.length === 0) return;
    setParcelsBusy(true);
    setParcelsErr(null);
    try {
      await savedParcels.bulkAdd(data.id, toAdd);
      reloadSavedParcels(data.id);
    } catch (e) {
      setParcelsErr(e instanceof Error ? e.message : String(e));
    } finally {
      setParcelsBusy(false);
    }
  };

  const removeSavedParcel = async (egibId: string) => {
    if (!data) return;
    setParcelToggleBusy(egibId);
    setParcelsErr(null);
    try {
      await savedParcels.remove(data.id, egibId);
      reloadSavedParcels(data.id);
    } catch (e) {
      setParcelsErr(e instanceof Error ? e.message : String(e));
    } finally {
      setParcelToggleBusy(null);
    }
  };

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!data) return <div className="text-ink-400">Ładowanie…</div>;

  const decydentPill = data.authority === 'REGIONAL' ? 'pill-marsz' : 'pill-star';
  const selectedBoreholeId = profileFor ?? data.boreholes[0]?.id ?? null;
  const selectedBorehole = data.boreholes.find(b => b.id === selectedBoreholeId) ?? data.boreholes[0];
  const layers: BoreholeLayer[] = selectedBorehole?.layers ?? [];
  const maxDepth = layers.reduce((m, w) => Math.max(m, w.toDepth), 0);

  const openCreate = () => { setEditingBorehole(null); setModalOpen(true); };
  const openEdit = (b: Borehole) => { setEditingBorehole(b); setModalOpen(true); };

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="m-0">{data.name}</h1>
            <span className={'pill ' + decydentPill}><span className="pill-dot"/>{data.authorityName}</span>
            <KindEditor intake={data} onChanged={reload}/>
          </div>
          <div className="text-ink-400 text-[13px]">
            {data.number} · {data.client.name} · Q={data.qM3h ?? '—'} m³/h
            {data.town && <> · {data.town}{data.county ? ', pow. ' + data.county : ''}</>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canCreateStudy && (
            <button onClick={() => setNewStudyOpen(true)} className="btn btn-primary btn-sm"
                    title="Utwórz opracowanie poza ścieżką oferta→umowa/zlecenie (np. dla stałego klienta)">
              <Icon name="plus" className="w-3.5 h-3.5"/> Nowe opracowanie
            </button>
          )}
          {canEditIntake && data.status !== 'ARCHIVED' && (
            <button onClick={() => setMergeOpen(true)} className="btn btn-secondary btn-sm"
                    title="Nazwa całego ujęcia, studnie zamiast ujęć-studni, scalenie z innymi ujęciami klienta">
              Uporządkuj / scal
            </button>
          )}
          <NavLink to="/intakes" className="btn btn-secondary btn-sm">← Wszystkie ujęcia</NavLink>
        </div>
      </header>

      <IntakeMergeModal open={mergeOpen} intake={data} onClose={() => setMergeOpen(false)}
                        onMerged={() => { setMergeOpen(false); reload(); }}/>

      <NewStudyModal
        open={newStudyOpen}
        defaultIntakeId={data.id}
        onClose={() => setNewStudyOpen(false)}
        onCreated={(sid) => { setNewStudyOpen(false); nav(`/studies/${sid}`); }}
      />

      <div className="grid grid-cols-4 gap-3.5 mb-6">
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/>
          <div className="kpi-label">Otwory</div><div className="kpi-value">{data.boreholes.length}</div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-earth-500"/>
          <div className="kpi-label">Opracowania</div><div className="kpi-value">{data.studies.length}</div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-water-500"/>
          <div className="kpi-label">Wydajność Q</div><div className="kpi-value">{data.qM3h ?? '—'}<span className="text-ink-400 text-[12px] ml-1">m³/h</span></div></div>
        <div className="kpi"><div className="absolute inset-y-0 left-0 w-[3px] bg-ink-400"/>
          <div className="kpi-label">Od</div><div className="kpi-value text-[18px]">{data.validFrom ?? '—'}</div></div>
      </div>

      {/* Pasek zakładek — mniej na jednym ekranie: dane podstawowe / otwory / mapa+działki / dokumenty / powiązania / analiza sitowa.
          Badge przy „Analizie sitowej" pojawia się dopiero po pobraniu listy: licznik nie jest znany
          z IntakeDto, a strzelanie po niego przy wejściu na kartę ujęcia kosztowałoby dodatkowe zapytanie
          u każdego użytkownika, także tego, który w tę zakładkę nigdy nie zajrzy. Zakładka bez badge'a
          jest tu normą (tak działa „Dokumenty"), więc brak licznika przed pierwszym otwarciem niczego nie psuje. */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-ink-100">
        {([
          { key: 'przeglad', label: 'Przegląd', icon: 'layout' },
          { key: 'otwory', label: 'Otwory i profil', icon: 'droplet', badge: data.boreholes.length },
          { key: 'mapa', label: 'Mapa i działki', icon: 'map', badge: savedParcelsList.length },
          { key: 'dokumenty', label: 'Dokumenty', icon: 'folder' },
          { key: 'powiazania', label: 'Powiązania', icon: 'file-text', badge: data.studies.length },
          ...(canViewSieve
            ? [{ key: 'sitowa', label: 'Analiza sitowa', icon: 'mesh', badge: sieveList?.length }]
            : []),
        ] as { key: IntakeTab; label: string; icon: string; badge?: number }[]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={'flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold border-b-2 -mb-px whitespace-nowrap transition ' +
              (tab === t.key ? 'border-water-500 text-water-700' : 'border-transparent text-ink-400 hover:text-ink-700')}>
            <Icon name={t.icon} className="w-4 h-4"/>
            {t.label}
            {t.badge != null && t.badge > 0 && (
              <span className={'ml-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full text-[11px] font-bold ' +
                (tab === t.key ? 'bg-water-500 text-white' : 'bg-ink-100 text-ink-500')}>{t.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* === PRZEGLĄD — dane podstawowe === */}
      {tab === 'przeglad' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
          <NavLink to={`/klienci/${data.client.id}`}
                   className="card no-underline text-inherit hover:border-water-500 hover:shadow-sm transition group">
            <div className="card-head">
              <div className="card-title">Klient</div>
              <Icon name="chevron" className="ml-auto w-3.5 h-3.5 text-ink-300 group-hover:text-water-500"/>
            </div>
            <div className="card-body">
              <div className="font-semibold text-ink-900 flex items-center gap-1.5">
                <Icon name="users" className="w-4 h-4 text-water-600"/>
                {data.client.name}
              </div>
              {data.client.nip && <div className="text-[12px] text-ink-500 mt-1">NIP: <span className="font-mono text-ink-700">{data.client.nip}</span></div>}
              {data.client.city && <div className="text-[12px] text-ink-500">{data.client.city}</div>}
              {data.client.email && <div className="text-[12px] text-ink-500 truncate" title={data.client.email}>{data.client.email}</div>}
            </div>
          </NavLink>

          <div className="card">
            <div className="card-head"><div className="card-title">Decydent</div></div>
            <div className="card-body">
              <span className={'pill ' + decydentPill}><span className="pill-dot"/>{data.authorityName}</span>
              {data.authorityJustification && (
                <div className="text-[12px] text-ink-500 mt-2">{data.authorityJustification}</div>
              )}
            </div>
          </div>

          <LocationCard intake={data} onChanged={reload}/>
          <ZarzadZlewniCard intake={data} onChanged={reload}/>

          <div className="md:col-span-2">
            <IntakeOperatCard intake={data} onChanged={reload}/>
          </div>
        </div>
      )}

      {/* === OTWORY I PROFIL === */}
      {tab === 'otwory' && (
        <div className="flex flex-col gap-4">
          <div className="card">
            <div className="card-head">
              <div className="card-title">Otwory</div>
              <button onClick={openCreate} className="ml-auto btn btn-primary btn-sm">
                <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj otwór
              </button>
            </div>
            <div className="card-body !p-0">
              {data.boreholes.length === 0 && (
                <div className="text-ink-400 p-4 text-center">
                  Brak otworów. Kliknij <strong>Dodaj otwór</strong> by stworzyć pierwszy z profilem geologicznym.
                </div>
              )}
              {data.boreholes.length > 0 && (
                <table className="table-hw">
                  <thead><tr><th>Oznaczenie</th><th>Funkcja</th><th>Głębokość</th><th>Współrzędne</th><th>Wydajność</th><th>Warstwy</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {data.boreholes.map(o => (
                      <tr key={o.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => openEdit(o)}>
                        <td className="font-mono text-ink-800">{o.designation}</td>
                        <td><span className="pill pill-water">{o.functionAbbreviation}</span></td>
                        <td className="font-mono tabular-nums">{o.totalDepth ?? '—'} m</td>
                        <td className="font-mono tabular-nums text-[11px] text-ink-600">
                          {o.latitude != null && o.longitude != null
                            ? `${Number(o.latitude).toFixed(5)}, ${Number(o.longitude).toFixed(5)}`
                            : '—'}
                        </td>
                        <td className="font-mono tabular-nums">{o.yieldM3h ?? '—'} m³/h</td>
                        <td className="text-[12px] text-ink-700">
                          {o.layers.length > 0
                            ? <span className="font-mono">{o.layers.length} warstw{o.layers.length === 1 ? 'a' : ''}</span>
                            : <span className="text-ink-400 italic">brak profilu</span>}
                        </td>
                        <td className="text-ink-500 text-[12px]">{o.status}</td>
                        <td><Icon name="edit" className="w-3 h-3 text-ink-400"/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div className="card-title">Profil geologiczny</div>
              {data.boreholes.length > 1 && (
                <SearchableSelect value={selectedBoreholeId != null ? String(selectedBoreholeId) : ''}
                        onChange={v => setProfileFor(v ? Number(v) : null)}
                        className="select-sm ml-3 text-[11px]">
                  {data.boreholes.map(b => (
                    <option key={b.id} value={b.id}>{b.designation}</option>
                  ))}
                </SearchableSelect>
              )}
              <span className="text-ink-400 text-[12px] ml-auto">
                {selectedBorehole ? `${selectedBorehole.designation} · ${layers.length > 0 ? `0–${maxDepth} m` : 'brak warstw'}` : '—'}
              </span>
              {selectedBorehole && (
                <button onClick={() => openEdit(selectedBorehole)} className="ml-2 btn btn-secondary btn-sm">
                  <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj profil
                </button>
              )}
              {selectedBorehole && <BoreholeCardActions boreholeId={selectedBorehole.id} onSaved={reload}/>}
            </div>
            <div className="card-body">
              {!selectedBorehole && (
                <div className="text-ink-400 text-[12px] py-6 text-center">
                  Brak otworów — dodaj pierwszy, by zacząć budować profil geologiczny.
                </div>
              )}
              {selectedBorehole && layers.length === 0 && (
                <div className="text-ink-400 text-[12px] py-6 text-center">
                  Otwór <strong>{selectedBorehole.designation}</strong> nie ma jeszcze warstw.
                  Kliknij <strong>Edytuj profil</strong>, by je dodać.
                </div>
              )}
              {layers.length > 0 && (
                <div className="flex gap-4">
                  <div className="flex-1 flex flex-col" style={{ height: 300 }}>
                    {layers.map(w => {
                      const h = ((w.toDepth - w.fromDepth) / maxDepth) * 100;
                      return (
                        <div
                          key={w.id}
                          className="border-b border-white/50 flex items-center justify-between px-3 text-[11px] font-mono font-bold"
                          style={{
                            height: h + '%',
                            color: 'rgba(0,0,0,0.75)',
                            textShadow: '0 0 4px rgba(255,255,255,0.7)',
                            ...patternStyle(w.pattern, w.color),
                          }}
                          title={w.description}
                        >
                          <span>{w.symbol} · {w.description}</span>
                          <span>{w.fromDepth}–{w.toDepth} m</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="w-12 flex flex-col justify-between text-[10px] font-mono text-ink-400" style={{ height: 300 }}>
                    <span>0 m</span>
                    <span>{Math.round(maxDepth/2)} m</span>
                    <span>{maxDepth} m</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* === MAPA I DZIAŁKI — pełna szerokość, mapa i działki EGIB obok siebie z zapasem === */}
      {tab === 'mapa' && (() => {
        // Środek mapy: współrzędne ujęcia, fallback na pierwszy otwór z lat/lng.
        const intakeCenter: [number, number] | undefined =
          data.latitude != null && data.longitude != null
            ? [Number(data.longitude), Number(data.latitude)]
            : (() => {
                const o = data.boreholes?.find(b => b.latitude != null && b.longitude != null);
                return o ? [Number(o.longitude), Number(o.latitude)] as [number, number] : undefined;
              })();
        const hasIntakeCoords = data.latitude != null && data.longitude != null;
        // Panel działek pokazujemy też, gdy są zapisane działki bez współrzędnych ujęcia —
        // inaczej zapisane działki byłyby klikalne (i usuwalne) na mapie bez żadnego UI do zarządzania nimi.
        const showParcels = hasIntakeCoords || savedParcelsList.length > 0;
        return (
          <div className={'grid gap-4 items-start ' + (showParcels ? 'grid-cols-1 lg:grid-cols-[1.7fr_1fr]' : 'grid-cols-1')}>
            <div className="card">
              <div className="card-head">
                <div className="card-title">Mapa</div>
                {intakeCenter && (
                  <>
                    <button onClick={() => setShowPshMap(v => !v)}
                            className={'ml-auto btn btn-sm ' + (showPshMap ? 'btn-primary' : 'btn-secondary')}
                            title="Otwory PSH (CBDG PIG-PIB) w okolicy — kliknij otwór, aby otworzyć kartę">
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-1.5 align-middle" style={{ background: '#b45309', border: '1.5px solid #fff', boxShadow: '0 0 0 1px #b45309' }}/>
                      Otwory PSH
                    </button>
                    <span className="ml-2 text-[11px] text-ink-500 font-mono">
                      {intakeCenter[1].toFixed(5)}, {intakeCenter[0].toFixed(5)}
                    </span>
                  </>
                )}
              </div>
              <div className="card-body !p-0">
                {intakeCenter ? (
                  <MapLibreMap
                    intake={data}
                    center={intakeCenter}
                    zoom={14}
                    height="600px"
                    showPsh={showPshMap}
                    parcelsOverlay={mergedParcelsOverlay}
                    savedParcelIds={savedParcelIdSet}
                    focusedParcelId={focusedParcelId}
                    onParcelClick={onParcelClickToggle}
                    bufferCircle={showBufferCircle && data.latitude != null && data.longitude != null
                      ? { lon: Number(data.longitude), lat: Number(data.latitude),
                          radiusM: parcelBufferM, color: '#06B6D4' }
                      : null}
                  />
                ) : (
                  <div className="p-6 text-center text-[12px] text-ink-400">
                    <Icon name="map" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
                    Brak współrzędnych ujęcia ani otworów.<br/>
                    Edytuj ujęcie i wpisz szerokość/długość geograficzną, aby zobaczyć je na mapie.
                  </div>
                )}
              </div>
            </div>

            {showParcels && (
              <IntakeParcelsCard
                intakeId={data.id}
                savedList={savedParcelsList}
                searched={searchedParcels}
                bufferM={parcelBufferM}
                onBufferChange={setParcelBufferM}
                showBuffer={showBufferCircle}
                onShowBufferChange={setShowBufferCircle}
                busy={parcelsBusy}
                toggleBusyEgibId={parcelToggleBusy}
                err={parcelsErr}
                onSearch={searchParcels}
                onAddAll={addAllFromSearch}
                onRemoveSaved={removeSavedParcel}
                onAddOne={(f) => onParcelClickToggle(f.properties as unknown as Record<string, string | number | null>)}
                focusedEgibId={focusedParcelId}
                onFocus={(egibId) => setFocusedParcelId(cur => cur === egibId ? null : egibId)}
              />
            )}
          </div>
        );
      })()}

      {/* === DOKUMENTY === */}
      {tab === 'dokumenty' && (
        <ArchiveFilesCard scope="intake" scopeId={data.id} scopeName={data.name}/>
      )}

      {/* === POWIĄZANIA — historia opracowań === */}
      {tab === 'powiazania' && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">Historia opracowań</div>
            {canCreateStudy && (
              <button onClick={() => setNewStudyOpen(true)} className="ml-auto btn btn-secondary btn-sm">
                <Icon name="plus" className="w-3.5 h-3.5"/> Nowe opracowanie
              </button>
            )}
          </div>
          <div className="card-body !px-[18px] !py-1.5">
            {data.studies.length === 0 && <div className="text-ink-400 py-3">Brak opracowań.</div>}
            {data.studies.map(o => (
              <NavLink key={o.id} to={`/studies/${o.id}`} className="flex items-center gap-3 py-3 border-b border-ink-100 last:border-b-0 no-underline text-inherit hover:bg-ink-50 -mx-[18px] px-[18px]">
                <div className="w-9 h-9 rounded-lg bg-earth-500/10 text-earth-500 grid place-items-center shrink-0">
                  <Icon name="file-text"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] text-ink-900">{o.title}</div>
                  <div className="text-[11px] text-ink-400">{o.number} · {o.typeName}</div>
                </div>
                <span className={'pill pill-' + (o.statusColor || 'water')}>{o.statusName}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {/* === ANALIZA SITOWA — analizy uziarnienia i dobór obsypki dla tego ujęcia === */}
      {tab === 'sitowa' && canViewSieve && (
        <div className="card">
          <div className="card-head">
            <div className="card-title">Analizy sitowe ujęcia</div>
            <NavLink to={`/analiza-sitowa?intakeId=${data.id}`}
                     className="ml-auto text-[12px] text-water-600 hover:underline">
              Wszystkie analizy ujęcia →
            </NavLink>
          </div>
          <div className="card-body !px-[18px] !py-1.5">
            {sieveBusy && sieveList === null && (
              <div className="text-ink-400 py-3">Ładowanie analiz…</div>
            )}
            {sieveErr && (
              <div className="my-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2 flex items-center gap-2 flex-wrap">
                <span className="flex-1 min-w-0">Nie udało się pobrać analiz sitowych: {sieveErr}</span>
                <button onClick={() => { if (id) loadSieve(id); }} disabled={sieveBusy}
                        className="btn btn-secondary btn-sm">
                  {sieveBusy ? 'Ponawiam…' : 'Spróbuj ponownie'}
                </button>
              </div>
            )}
            {sieveList !== null && sieveList.length === 0 && !sieveErr && (
              <div className="py-6 text-center text-[12px] text-ink-400">
                <Icon name="mesh" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
                To ujęcie nie ma jeszcze żadnej analizy sitowej.
                {canEditSieve && (
                  <div className="mt-3">
                    <NavLink to="/analiza-sitowa" className="btn btn-primary btn-sm no-underline">
                      <Icon name="plus" className="w-3.5 h-3.5"/> Załóż analizę sitową
                    </NavLink>
                  </div>
                )}
              </div>
            )}
            {(sieveList ?? []).map(s => (
              <NavLink key={s.id} to={`/analiza-sitowa/${s.id}`}
                       className="flex items-center gap-3 py-3 border-b border-ink-100 last:border-b-0 no-underline text-inherit hover:bg-ink-50 -mx-[18px] px-[18px]">
                <div className="w-9 h-9 rounded-lg bg-water-500/10 text-water-600 grid place-items-center shrink-0">
                  <Icon name="mesh"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] text-ink-900 truncate">{s.name}</div>
                  <div className="text-[11px] text-ink-400">
                    {[
                      s.docNo,
                      s.well ? `otwór ${s.well}` : null,
                      `${s.sampleCount} ${probkiPl(s.sampleCount)}`,
                      s.studyDate,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <span className={'pill ' + sieveStatusPill(s.status)}>{s.statusName}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      <BoreholeProfileModal
        open={modalOpen}
        intakeId={data.id}
        borehole={editingBorehole}
        existingDesignations={data.boreholes
          .filter(b => b.id !== editingBorehole?.id)
          .map(b => b.designation)}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); reload(); }}
      />
    </div>
  );
}

function ZarzadZlewniCard({ intake, onChanged }: { intake: Intake; onChanged: () => void }) {
  const [list, setList] = useState<ZarzadZlewniDto[]>([]);
  const [editing, setEditing] = useState(false);
  const [pendingId, setPendingId] = useState<number | ''>(intake.zarzadZlewni?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => { zarzadyZlewni.list().then(setList).catch(() => { /* nonfatal */ }); }, []);
  useEffect(() => { setPendingId(intake.zarzadZlewni?.id ?? ''); }, [intake.zarzadZlewni?.id]);

  const detect = async () => {
    if (intake.latitude == null || intake.longitude == null) {
      setInfo('Brak współrzędnych ujęcia — uzupełnij współrzędne i ponów.');
      return;
    }
    try {
      const res = await zarzadyZlewni.byCoords(intake.latitude, intake.longitude, intake.voivodeship);
      if (res.match) {
        setPendingId(res.match.id);
        setInfo(`Wykryto: ${res.match.name}. ${res.reason}`);
      } else {
        setInfo(res.reason + (res.candidates.length > 0
          ? ` Kandydaci: ${res.candidates.map(c => c.name).join(', ')}.` : ''));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await intakes.setZarzadZlewni(intake.id, pendingId === '' ? null : Number(pendingId));
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const zz = intake.zarzadZlewni;

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Zarząd Zlewni Wód Polskich</div>
        {!editing && (
          <button onClick={() => { setEditing(true); setInfo(null); setErr(null); }}
                  className="ml-auto text-water-600 hover:text-water-800 p-1" title="Edytuj">
            <Icon name="edit" className="w-3.5 h-3.5"/>
          </button>
        )}
      </div>
      <div className="card-body text-[12px] text-ink-600">
        {!editing && (zz ? (
          <div className="space-y-1">
            <div className="text-ink-800 font-semibold">{zz.name}</div>
            <div className="text-ink-500">{zz.rzgwName}</div>
            {zz.addressLine1 && (
              <div className="font-mono text-ink-700">
                {zz.addressLine1}
                {(zz.addressPostalCode || zz.addressCity) && <>, {zz.addressPostalCode} {zz.addressCity}</>}
              </div>
            )}
            {!zz.addressLine1 && (
              <div className="text-ink-400 italic text-[11px]">Adres ZZ nieuzupełniony — wniosek użyje placeholderów.</div>
            )}
          </div>
        ) : (
          <div className="text-ink-400 italic">
            Nie wskazano. Bez wskazania ZZ wniosek wodnoprawny pokaże placeholdery do uzupełnienia.
          </div>
        ))}
        {editing && (
          <div className="space-y-2">
            <SearchableSelect
              value={pendingId === '' ? '' : String(pendingId)}
              onChange={v => setPendingId(v === '' ? '' : Number(v))}
              disabled={busy}
              className="w-full"
            >
              <option value="">— nie wskazany —</option>
              {(() => {
                const groups: Record<string, ZarzadZlewniDto[]> = {};
                list.forEach(z => {
                  if (!groups[z.rzgwName]) groups[z.rzgwName] = [];
                  groups[z.rzgwName].push(z);
                });
                return Object.keys(groups).sort().map(rzgw => (
                  <optgroup key={rzgw} label={rzgw}>
                    {groups[rzgw].map(z => (
                      <option key={z.id} value={z.id}>{z.name}</option>
                    ))}
                  </optgroup>
                ));
              })()}
            </SearchableSelect>
            <div className="flex gap-2 flex-wrap">
              <button type="button" onClick={detect} disabled={busy}
                      className="btn btn-secondary btn-sm"
                      title="Heurystyka po województwie. Pełne zlewniowe dopasowanie sprawdź na hydroportal ISOK">
                <Icon name="map" className="w-3.5 h-3.5"/> Wykryj z współrzędnych
              </button>
              <button type="button" onClick={save} disabled={busy} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5"/> Zapisz
              </button>
              <button type="button" onClick={() => { setEditing(false); setPendingId(zz?.id ?? ''); setInfo(null); }}
                      disabled={busy} className="btn btn-secondary btn-sm">Anuluj</button>
            </div>
            {info && <div className="text-[11px] text-ink-600 bg-ink-50 border border-ink-100 rounded p-2">{info}</div>}
            {err && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

const PARCEL_BUFFERS = [50, 100, 200, 500, 1000] as const;
/** Limity zgodne z backend-em (EgibApi.parcelsForIntake): clamp 10–2000m. */
const PARCEL_BUFFER_MIN = 10;
const PARCEL_BUFFER_MAX = 2000;

/** Odmiana „działka" przez liczebnik: 1 działkę, 2–4 działki, 5+ (i 12–14) działek. */
function dzialkiPl(n: number): string {
  if (n === 1) return 'działkę';
  const dwie = n % 10, nascie = n % 100;
  return dwie >= 2 && dwie <= 4 && (nascie < 12 || nascie > 14) ? 'działki' : 'działek';
}

interface IntakeParcelsCardProps {
  intakeId: number;
  savedList: SavedParcelDto[];
  searched: EgibParcelFeatureCollection | null;
  bufferM: number;
  onBufferChange: (m: number) => void;
  showBuffer: boolean;
  onShowBufferChange: (v: boolean) => void;
  busy: boolean;
  toggleBusyEgibId: string | null;
  err: string | null;
  onSearch: () => void;
  onAddAll: () => void;
  onRemoveSaved: (egibId: string) => void;
  onAddOne: (feature: EgibParcelFeatureCollection['features'][number]) => void;
  focusedEgibId?: string | null;
  onFocus?: (egibId: string) => void;
}

function IntakeParcelsCard(p: IntakeParcelsCardProps) {
  const savedIds = new Set(p.savedList.map(s => s.egibId));
  const newInBuffer = (p.searched?.features ?? []).filter(f => !savedIds.has(f.id));
  const totalSavedM2 = p.savedList.reduce((s, sp) => s + (sp.poleM2 ?? 0), 0);
  const totalRows = p.savedList.length + newInBuffer.length;

  // Custom-bufor: lokalny stan inputa, commit na blur/Enter. Pozwala wpisać dowolną
  // wartość spoza presetów (np. 350m). Po commicie wartość przechodzi przez clamp [10, 2000]
  // identyczny jak na backendzie (EgibApi.parcelsForIntake).
  const isPreset = (PARCEL_BUFFERS as readonly number[]).includes(p.bufferM);
  const [customDraft, setCustomDraft] = useState<string>(isPreset ? '' : String(p.bufferM));
  // Sync z parentem gdy bufferM zmienia się z zewnątrz (np. kliknięcie presetu).
  useEffect(() => {
    setCustomDraft(isPreset ? '' : String(p.bufferM));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.bufferM]);
  // Eksport tabeli (CSV/XLSX) — wysyłamy dokładnie te wiersze, które widać na ekranie:
  // najpierw działki w operacie, potem nowe znalezione w buforze. Kolumna „W operacie"
  // w pliku rozróżnia oba zbiory, więc odbiorca może je odfiltrować w arkuszu.
  const [exportBusy, setExportBusy] = useState<'csv' | 'xlsx' | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const buildExportRows = (): ParcelExportRow[] => [
    ...p.savedList.map(sp => ({
      egibId: sp.egibId,
      parcelNumber: sp.parcelNumber,
      obrebNumer: sp.obrebNumer,
      obrebNazwa: sp.obrebNazwa,
      gminaNazwa: sp.gminaNazwa,
      poleM2: sp.poleM2,
      klasouzytki: sp.klasouzytki,
      dataEgib: sp.dataEgib,
      saved: true,
    })),
    ...newInBuffer.map(f => {
      const m2 = Number.parseFloat(f.properties.poleEwidencyjne ?? '');
      return {
        egibId: f.id,
        parcelNumber: f.properties.number,
        obrebNumer: f.properties.obrebNumer,
        obrebNazwa: f.properties.obrebNazwa,
        gminaNazwa: f.properties.gminaNazwa,
        poleM2: Number.isFinite(m2) ? m2 : null,
        klasouzytki: f.properties.klasouzytki,
        dataEgib: f.properties.dataEgib,
        saved: false,
      };
    }),
  ];

  const doExport = async (format: 'csv' | 'xlsx') => {
    const rows = buildExportRows();
    if (rows.length === 0) return;
    setExportBusy(format);
    setExportErr(null);
    try {
      await egib.exportParcels(p.intakeId, format, { bufferM: p.bufferM, rows });
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : String(e));
    } finally {
      setExportBusy(null);
    }
  };

  const commitCustomBuffer = () => {
    const raw = customDraft.trim();
    if (raw === '') return;  // pusty input — nie zmieniamy nic
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) {
      setCustomDraft(isPreset ? '' : String(p.bufferM));
      return;
    }
    const clamped = Math.max(PARCEL_BUFFER_MIN, Math.min(PARCEL_BUFFER_MAX, n));
    p.onBufferChange(clamped);
    setCustomDraft(String(clamped));
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Działki w obszarze badań (EGIB)</div>
        <span className="ml-2 text-[11px] text-ink-400">
          {p.savedList.length > 0
            ? <><span className="text-emerald-700 font-semibold">{p.savedList.length} w operacie</span>
                {totalSavedM2 > 0 && ` · ${(totalSavedM2 / 10000).toFixed(2)} ha`}</>
            : 'brak działek w operacie'}
          {newInBuffer.length > 0 && (
            <span className="ml-2 text-amber-700">+{newInBuffer.length} znalezionych w buforze</span>
          )}
        </span>
      </div>
      <div className="card-body !p-0">
        <div className="px-4 py-3 border-b border-ink-100 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] text-ink-500 uppercase tracking-wider font-semibold mr-1">Bufor:</span>
          {PARCEL_BUFFERS.map(b => (
            <button
              key={b}
              onClick={() => p.onBufferChange(b)}
              disabled={p.busy}
              className={'px-2 py-1 rounded text-[11px] border ' +
                (p.bufferM === b
                  ? 'border-ink-700 bg-ink-50 font-semibold text-ink-900'
                  : 'border-ink-200 text-ink-700 hover:border-ink-400')}
            >
              {b < 1000 ? `${b} m` : `${b / 1000} km`}
            </button>
          ))}
          <div className={'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border '
                + (!isPreset
                    ? 'border-ink-700 bg-ink-50'
                    : 'border-ink-200 hover:border-ink-400')}
               title="Wpisz własną wartość bufora (10–2000 m) — Enter aby zatwierdzić">
            <input
              type="number"
              min={PARCEL_BUFFER_MIN}
              max={PARCEL_BUFFER_MAX}
              step={10}
              value={customDraft}
              onChange={e => setCustomDraft(e.target.value)}
              onBlur={commitCustomBuffer}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commitCustomBuffer(); (e.target as HTMLInputElement).blur(); } }}
              disabled={p.busy}
              placeholder="np. 350"
              className={'w-[64px] bg-transparent text-[11px] font-mono text-right outline-none border-0 '
                + (!isPreset ? 'font-semibold text-ink-900' : 'text-ink-700')}
            />
            <span className="text-[11px] text-ink-500">m</span>
          </div>
          <label className="ml-2 flex items-center gap-1.5 text-[11px] text-ink-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={p.showBuffer}
              onChange={e => p.onShowBufferChange(e.target.checked)}
              className="w-3.5 h-3.5"
            />
            Pokaż bufor na mapie
          </label>
          <button
            onClick={p.onSearch}
            disabled={p.busy}
            className="ml-auto btn btn-secondary btn-sm"
          >
            {p.busy
              ? <><svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg> Szukam…</>
              : <><Icon name="search" className="w-3.5 h-3.5"/> Szukaj w buforze</>}
          </button>
          {newInBuffer.length > 0 && (
            <button
              onClick={p.onAddAll}
              disabled={p.busy}
              className="btn btn-primary btn-sm"
              title={`Dodaj wszystkie ${newInBuffer.length} znalezionych działek do operatu`}
            >
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj wszystkie ({newInBuffer.length})
            </button>
          )}
          {totalRows > 0 && (
            <div className="flex items-center gap-1 pl-2 ml-1 border-l border-ink-200">
              <span className="text-[11px] text-ink-500 uppercase tracking-wider font-semibold mr-0.5">
                Eksport:
              </span>
              {(['csv', 'xlsx'] as const).map(fmt => (
                <button
                  key={fmt}
                  onClick={() => doExport(fmt)}
                  disabled={p.busy || exportBusy !== null}
                  className="btn btn-secondary btn-sm"
                  title={`Pobierz jako ${fmt.toUpperCase()}: ${totalRows} ${dzialkiPl(totalRows)} `
                    + `(${p.savedList.length} w operacie`
                    + (newInBuffer.length > 0
                        ? `, ${newInBuffer.length} znalezionych w buforze ${p.bufferM} m)`
                        : ')')}
                >
                  {exportBusy === fmt
                    ? <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25"/>
                        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                      </svg>
                    : <Icon name="download" className="w-3.5 h-3.5"/>}
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        {p.err && <div className="p-3 text-red-600 text-[12px]">{p.err}</div>}
        {exportErr && <div className="p-3 text-red-600 text-[12px]">Eksport nie powiódł się: {exportErr}</div>}
        {p.searched?.meta?.note && (
          <div className="p-3 text-amber-700 text-[12px] bg-amber-50/50">{p.searched.meta.note}</div>
        )}

        {p.savedList.length === 0 && newInBuffer.length === 0 && !p.busy && (
          <div className="p-6 text-center text-[12px] text-ink-400">
            <Icon name="map" className="w-8 h-8 mx-auto mb-2 text-ink-300"/>
            Brak działek. Kliknij <strong>Szukaj w buforze</strong>, aby pobrać działki z EGIB GUGiK,
            albo kliknij polygon na mapie wybierając konkretną działkę.
          </div>
        )}

        {(p.savedList.length > 0 || newInBuffer.length > 0) && (
          <table className="table-hw">
            <thead>
              <tr>
                <th className="w-8">W&nbsp;op.</th>
                <th>Numer</th>
                <th>Obręb / gmina</th>
                <th className="text-right">Powierzchnia</th>
                <th>ID EGiB</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {/* Działki w operacie (zapisane) */}
              {p.savedList.map(sp => {
                const m2 = sp.poleM2 ?? null;
                const ha = m2 != null ? m2 / 10000 : null;
                const busyRow = p.toggleBusyEgibId === sp.egibId;
                return (
                  <tr key={'saved-' + sp.egibId}
                      onClick={() => p.onFocus?.(sp.egibId)}
                      title="Kliknij, aby podświetlić działkę na mapie"
                      className={'cursor-pointer ' + (p.focusedEgibId === sp.egibId
                        ? 'bg-blue-100 ring-1 ring-inset ring-blue-400'
                        : 'bg-emerald-50/40 hover:bg-emerald-50')}>
                    <td className="text-center">
                      <span className="inline-block w-3 h-3 rounded-sm bg-emerald-600" title="Zapisana w operacie"/>
                    </td>
                    <td className="font-mono font-semibold text-[13px]">{sp.parcelNumber ?? '—'}</td>
                    <td className="text-[12px]">
                      {sp.obrebNazwa && <div>obr. {sp.obrebNazwa} {sp.obrebNumer && <span className="text-ink-400">({sp.obrebNumer})</span>}</div>}
                      {sp.gminaNazwa && <div className="text-ink-500 text-[11px]">{sp.gminaNazwa}</div>}
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      {m2 != null ? (
                        <>
                          <div>{m2.toFixed(0)} m²</div>
                          {ha !== null && ha >= 0.01 && <div className="text-[10px] text-ink-400">{ha.toFixed(3)} ha</div>}
                        </>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="font-mono text-[10px] text-ink-500">{sp.egibId}</td>
                    <td className="text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); p.onRemoveSaved(sp.egibId); }}
                        disabled={busyRow || p.busy}
                        className="text-ink-400 hover:text-red-600 p-1"
                        title="Usuń z operatu"
                      >
                        <Icon name="x" className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {/* Działki znalezione w buforze (jeszcze nie zapisane) */}
              {newInBuffer.map(f => {
                const ff = f.properties;
                const m2 = parseFloat(ff.poleEwidencyjne ?? '');
                const ha = Number.isFinite(m2) ? m2 / 10000 : null;
                const busyRow = p.toggleBusyEgibId === f.id;
                return (
                  <tr key={'new-' + f.id}
                      onClick={() => p.onFocus?.(f.id)}
                      title="Kliknij, aby podświetlić działkę na mapie"
                      className={'cursor-pointer ' + (p.focusedEgibId === f.id
                        ? 'bg-blue-100 ring-1 ring-inset ring-blue-400'
                        : 'hover:bg-ink-50')}>
                    <td className="text-center">
                      <span className="inline-block w-3 h-3 rounded-sm bg-amber-500/60" title="Znaleziona w buforze (nie dodana)"/>
                    </td>
                    <td className="font-mono font-semibold text-[13px]">{ff.number ?? '—'}</td>
                    <td className="text-[12px]">
                      {ff.obrebNazwa && <div>obr. {ff.obrebNazwa} {ff.obrebNumer && <span className="text-ink-400">({ff.obrebNumer})</span>}</div>}
                      {ff.gminaNazwa && <div className="text-ink-500 text-[11px]">{ff.gminaNazwa}</div>}
                    </td>
                    <td className="text-right font-mono tabular-nums">
                      {Number.isFinite(m2) ? (
                        <>
                          <div>{m2.toFixed(0)} m²</div>
                          {ha !== null && ha >= 0.01 && <div className="text-[10px] text-ink-400">{ha.toFixed(3)} ha</div>}
                        </>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="font-mono text-[10px] text-ink-500">{f.id}</td>
                    <td className="text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); p.onAddOne(f); }}
                        disabled={busyRow || p.busy}
                        className="text-ink-400 hover:text-emerald-600 p-1"
                        title="Dodaj do operatu"
                      >
                        <Icon name="plus" className="w-3.5 h-3.5"/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="px-4 py-2 border-t border-ink-100 text-[10px] text-ink-400 italic">
          Klik na działkę na mapie = toggle (dodaj/usuń z operatu). Klik w wiersz listy = podświetl działkę na mapie (niebieska).
          Zapisane = zielone, znalezione w buforze = amber. Lista trafia do tabeli "Działki w obszarze badań" w PDF/DOCX operatu.
        </div>
      </div>
    </div>
  );
}


/** Badge rodzaju ujęcia (nowe/istniejące) z inline-edycją w nagłówku.
 *  Zapis natychmiastowy po wyborze. Edycja tylko z uprawnieniem INTAKE_EDIT. */
function KindEditor({ intake, onChanged }: { intake: Intake; onChanged: () => void }) {
  const { user } = useAuth();
  const canEdit = user?.permissions?.includes('INTAKE_EDIT') ?? false;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<'NEW' | 'EXISTING' | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const save = async (kind: 'NEW' | 'EXISTING') => {
    if (kind === intake.kind) { setEditing(false); return; }
    setBusy(kind); setErr(null);
    try {
      await intakes.setKind(intake.id, kind);
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(null); }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <span className={'inline-block px-2 py-0.5 rounded text-[11px] font-semibold ' +
          (intake.kind === 'EXISTING' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700')}>
          {intake.kindName}
        </span>
        {canEdit && (
          <button onClick={() => { setErr(null); setEditing(true); }}
                  className="text-water-600 hover:text-water-800 p-0.5" title="Zmień rodzaj ujęcia">
            <Icon name="edit" className="w-3 h-3"/>
          </button>
        )}
      </span>
    );
  }

  const opt = (k: 'NEW' | 'EXISTING', label: string) => (
    <button type="button" disabled={busy !== null} onClick={() => save(k)}
      className={'px-2 py-0.5 rounded text-[11px] font-semibold border transition disabled:opacity-50 ' +
        (intake.kind === k ? 'border-water-500 bg-water-500/10 text-ink-800' : 'border-ink-200 text-ink-500 hover:border-ink-300')}>
      {busy === k ? '…' : label}
    </button>
  );

  return (
    <span className="inline-flex items-center gap-1">
      {opt('NEW', 'Nowe')}
      {opt('EXISTING', 'Istniejące')}
      <button type="button" onClick={() => setEditing(false)} className="text-ink-400 hover:text-ink-600 p-0.5" title="Anuluj">
        <Icon name="x" className="w-3 h-3"/>
      </button>
      {err && <span className="text-red-500 text-[10px]">{err}</span>}
    </span>
  );
}

/** Akcje karty otworu własnego: podgląd/pobranie PDF oraz zapis do archiwum systemu. */
function BoreholeCardActions({ boreholeId, onSaved }: { boreholeId: number; onSaved: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const navCard = useNavigate();
  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await boreholesApi.zapiszKarta(boreholeId);
      setMsg('Zapisano: ' + r.name);
      onSaved();
    } catch (e) {
      setMsg('Błąd: ' + (e instanceof Error ? e.message : String(e)));
    } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => navCard(`/otwory/${boreholeId}/karta`, { state: { backLabel: 'Ujęcie' } })} className="ml-2 btn btn-primary btn-sm"
              title="Karta otworu — edytor (Zestawienie/Projekt/Likwidacja/Rekonstrukcja) z wektorowym PDF">
        <Icon name="droplet" className="w-3.5 h-3.5"/> Karta otworu
      </button>
      <a href={boreholesApi.kartaUrl(boreholeId)} target="_blank" rel="noopener"
         className="ml-1 btn btn-secondary btn-sm" title="Stara, uproszczona karta otworu (PDF)">
        <Icon name="download" className="w-3.5 h-3.5"/> Karta (uproszcz.)
      </a>
      <button onClick={save} disabled={busy} className="ml-1 btn btn-secondary btn-sm"
              title="Wygeneruj i zapisz uproszczoną kartę otworu w archiwum systemu">
        <Icon name="check" className="w-3.5 h-3.5"/> {busy ? 'Zapisywanie…' : 'Zapisz (uproszcz.)'}
      </button>
      {msg && <span className="ml-2 text-[11px] text-ink-500">{msg}</span>}
    </>
  );
}

/** Karta z danymi lokalizacyjnymi ujęcia (miejscowość, powiat, województwo, lat/lng).
 *  Pozwala edytować współrzędne (WGS84 lub PUWG 2000) bez wchodzenia w wizard.
 *  Wymaga INTAKE_EDIT — przycisk "Edytuj" niewidoczny gdy brak uprawnień. */
function LocationCard({ intake, onChanged }: { intake: Intake; onChanged: () => void }) {
  const { user } = useAuth();
  const canEdit = user?.permissions?.includes("INTAKE_EDIT") ?? false;
  const [editing, setEditing] = useState(false);
  const [lat, setLat] = useState<number | null>(intake.latitude ?? null);
  const [lng, setLng] = useState<number | null>(intake.longitude ?? null);
  const [town, setTown] = useState(intake.town ?? '');
  const [voivodeship, setVoivodeship] = useState(intake.voivodeship ?? '');
  const [county, setCounty] = useState(intake.county ?? '');
  const [gmina, setGmina] = useState(intake.gmina ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [geoBusy, setGeoBusy] = useState(false);
  const [geoInfo, setGeoInfo] = useState<string | null>(null);

  const startEdit = () => {
    setLat(intake.latitude ?? null);
    setLng(intake.longitude ?? null);
    setTown(intake.town ?? '');
    setVoivodeship(intake.voivodeship ?? '');
    setCounty(intake.county ?? '');
    setGmina(intake.gmina ?? '');
    setErr(null); setGeoInfo(null);
    setEditing(true);
  };

  const detect = async () => {
    if (lat == null || lng == null) { setGeoInfo('Najpierw wpisz współrzędne.'); return; }
    setGeoBusy(true); setGeoInfo(null);
    try {
      const r = await geo.reverse(lat, lng);
      if (!r) { setGeoInfo('Nie znaleziono działki w tym punkcie.'); return; }
      if (r.voivodeship) setVoivodeship(r.voivodeship);
      if (r.county) setCounty(r.county);
      setGmina(r.voivodeship && r.county ? resolveGmina(r.voivodeship, r.county, r.gmina) : (r.gmina ?? ''));
      if (r.town) setTown(r.town);
      const dzialka = [r.parcelNumber && `dz. ${r.parcelNumber}`, r.obreb && `obręb ${r.obreb}`].filter(Boolean).join(', ');
      setGeoInfo('Wykryto: ' + [r.town, r.gmina, r.county, r.voivodeship].filter(Boolean).join(', ')
        + (dzialka ? ' · ' + dzialka : '') + (r.source ? ` (${r.source})` : ''));
    } catch (e) {
      setGeoInfo('Błąd geokodowania: ' + (e instanceof Error ? e.message : String(e)));
    } finally { setGeoBusy(false); }
  };

  const save = async () => {
    if ((lat == null) !== (lng == null)) { setErr("Podaj obie współrzędne albo żadną."); return; }
    setBusy(true); setErr(null);
    try {
      await intakes.updateLocation(intake.id, {
        latitude: lat, longitude: lng,
        town: town || null, voivodeship: voivodeship || null, county: county || null, gmina: gmina || null,
      });
      setEditing(false);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="card">
      <div className="card-head">
        <div className="card-title">Lokalizacja</div>
        {canEdit && !editing && (
          <button onClick={startEdit} className="ml-auto btn btn-secondary btn-sm">
            <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj
          </button>
        )}
      </div>
      <div className="card-body text-[12px] text-ink-600">
        {intake.town && <div><span className="text-ink-400">Miejscowość:</span> {intake.town}</div>}
        {intake.gmina && <div><span className="text-ink-400">Gmina:</span> {intake.gmina}</div>}
        {intake.county && <div><span className="text-ink-400">Powiat:</span> {intake.county}</div>}
        {intake.voivodeship && <div><span className="text-ink-400">Województwo:</span> {intake.voivodeship}</div>}

        {!editing && (intake.latitude || intake.longitude) && (
          <div className="font-mono mt-1">
            <span className="text-ink-400">WGS:</span> {intake.latitude?.toFixed(5)}, {intake.longitude?.toFixed(5)}
          </div>
        )}
        {!editing && !intake.latitude && !intake.longitude && canEdit && (
          <div className="text-amber-700 mt-1">
            Brak współrzędnych. Kliknij <strong>Edytuj</strong> aby uzupełnić.
          </div>
        )}

        {editing && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <label className="field col-span-2"><span className="field-label">Miejscowość</span>
              <input value={town} onChange={e => setTown(e.target.value)} className="input" disabled={busy}/></label>
            <label className="field"><span className="field-label">Województwo</span>
              <SearchableSelect value={voivodeship} disabled={busy}
                onChange={v => { setVoivodeship(v); if (county && !(COUNTIES[v]?.includes(county))) { setCounty(''); setGmina(''); } }}>
                <option value="">— wybierz województwo —</option>
                {VOIVODESHIPS.map(v => <option key={v} value={v}>{v}</option>)}
              </SearchableSelect>
            </label>
            <label className="field"><span className="field-label">Powiat</span>
              <SearchableSelect value={county} disabled={busy || !voivodeship}
                onChange={v => { setCounty(v); setGmina(''); }}>
                <option value="">{voivodeship ? '— wybierz powiat —' : '— najpierw województwo —'}</option>
                {(COUNTIES[voivodeship] ?? []).map(c => <option key={c} value={c}>{c}</option>)}
              </SearchableSelect>
            </label>
            <label className="field col-span-2"><span className="field-label">Gmina</span>
              <SearchableSelect value={gmina} onChange={setGmina} disabled={busy || !county} clearable allowFreeValue
                placeholder={county ? '— wybierz gminę —' : '— najpierw powiat —'}>
                {gminyFor(voivodeship, county).map(g => <option key={g} value={g}>{g}</option>)}
              </SearchableSelect>
            </label>
            <CoordsInput
              latitude={lat}
              longitude={lng}
              onChange={(la, ln) => { setLat(la); setLng(ln); }}
              layout="stacked"
              disabled={busy}
            />
            <div className="col-span-2">
              <button type="button" onClick={detect} disabled={geoBusy || busy || lat == null || lng == null}
                      className="btn btn-secondary btn-sm disabled:opacity-50"
                      title="Uzupełnia województwo/powiat/gminę z współrzędnych (GUGiK ULDK)">
                <Icon name="map" className="w-3.5 h-3.5"/> {geoBusy ? 'Wykrywanie…' : 'Wykryj z współrzędnych'}
              </button>
              {geoInfo && <div className="text-[11px] text-ink-600 mt-1">{geoInfo}</div>}
            </div>
            {err && <div className="col-span-2 text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}
            <div className="col-span-2 flex justify-end gap-2 mt-1">
              <button onClick={() => setEditing(false)} disabled={busy} className="btn btn-secondary btn-sm">
                Anuluj
              </button>
              <button onClick={save} disabled={busy} className="btn btn-primary btn-sm">
                <Icon name="check" className="w-3.5 h-3.5"/>
                {busy ? "Zapisywanie…" : "Zapisz"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
